import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';

export default function ForgotPassword() {
  const [step, setStep] = useState('request'); // 'request' or 'reset'
  const [username, setUsername] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await authAPI.forgotPassword(username);
      if (result.success) {
        toast.success('Check your email for a confirmation code.');
        setStep('reset');
      } else {
        toast.error(result.message || 'Unable to send reset code. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await authAPI.resetPassword(username, confirmationCode, newPassword);
      if (result.success) {
        toast.success('Password reset! You can now sign in.');
        navigate('/admin/login');
      } else {
        toast.error(result.message || 'Password reset failed. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
      }}
    >
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-cleona-600 px-4 py-2 mb-6">
            <span className="text-white font-manrope font-semibold text-sm">Smart Assistant™</span>
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
              <svg className="w-3.5 h-3.5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </span>
          </div>

          <h1 className="font-jakarta text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            {step === 'request' ? 'Reset Your Password' : 'Enter New Password'}
          </h1>
          <p className="text-gray-600 font-manrope">
            {step === 'request'
              ? 'Enter your username or email and we\'ll send you a code to reset your password.'
              : 'Enter the confirmation code from your email and choose a new password.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 'request' ? (
            <form onSubmit={handleRequest} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Username or Email
                </label>
                <input
                  id="username"
                  type="text"
                  required
                  placeholder="Enter your username or email"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Reset Code'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('reset')}
                className="w-full text-sm text-cleona-600 hover:text-cleona-700 font-manrope"
              >
                Already have a code? Enter it here
              </button>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Username or Email
                </label>
                <input
                  id="username"
                  type="text"
                  required
                  placeholder="Enter your username or email"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Confirmation Code
                </label>
                <input
                  id="confirmationCode"
                  type="text"
                  required
                  placeholder="Enter code from email"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  New Password ( min 8 characters, must include a capital letter and a special character )
                </label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  placeholder="New Password"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  placeholder="Confirm New Password"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('request')}
                className="w-full text-sm text-cleona-600 hover:text-cleona-700 font-manrope"
              >
                ← Request a new code
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-gray-600 font-manrope">
          Remember your password?{' '}
          <Link to="/admin/login" className="text-cleona-600 hover:text-cleona-700 font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
